<template>
  <v-card flat tile>
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <working-draft-help :openSection="openHelpSection"></working-draft-help>
    </res-dialog>
    <v-card-text>
      <div class="d-flex">
        <div class="flex-grow-1">
          <v-row>
            <v-col cols="12">
              <budget-page-header :title="'Working Draft'" :budgetIterationId="budgetIterationId"></budget-page-header>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <div class="mt-4 working-draft-container">
                <div class="grid-controls d-flex flex-wrap">
                  <div class="grid-radio-controls">
                    <res-radio-group label="Select View"
                                     labelColor="gray"
                                     :value="selectedViewOption"
                                     :options="viewOptions"
                                     @input="selectedViewOptionChanged">
                    </res-radio-group>
                  </div>
                  <div class="grid-radio-controls">
                    <res-radio-group label="Hide Zero Only Rows"
                                     labelColor="gray"
                                     :value="zeroRowsDisplayedSelection"
                                     :options="zeroRowsDisplayedRadioOptions"
                                     @input="zeroRowsDisplayedChanged">
                    </res-radio-group>
                  </div>
                  <div class="add-columns-controls">
                    <span class="add-columns-label">Add Columns</span>
                    <v-checkbox class="add-columns-option"
                                color="var(--v-resBlue-darken1)"
                                label="Months"
                                v-model="showMonths"
                                @change="toggleColumnVisibilities">
                      <template v-slot:label>
                        <div class="resBlue--text text--darken-1">
                          Months
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox class="add-columns-option"
                                color="var(--v-resBlue-darken1)"
                                label="Unit Details"
                                v-model="showUnitDetails"
                                @change="toggleColumnVisibilities">
                      <template v-slot:label>
                        <div class="resBlue--text text--darken-1">
                          Unit Details
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox class="add-columns-option"
                                color="var(--v-resBlue-darken1)"
                                label="Sq Ft Details"
                                v-model="showSqftDetails"
                                @change="toggleColumnVisibilities">
                      <template v-slot:label>
                        <div class="resBlue--text text--darken-1">
                          Sq Ft Details
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox class="add-columns-option"
                                color="var(--v-resBlue-darken1)"
                                label="Calculations"
                                v-model="showCalculations"
                                @change="toggleColumnVisibilities">
                      <template v-slot:label>
                        <div class="resBlue--text text--darken-1">
                          Calculations
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox class="add-columns-option"
                                color="var(--v-resBlue-darken1)"
                                label="Assumptions"
                                v-model="showAssumptions"
                                @change="toggleColumnVisibilities">
                      <template v-slot:label>
                        <div class="resBlue--text text--darken-1">
                          Assumptions
                        </div>
                      </template>
                    </v-checkbox>
                  </div>
                  <div class="help-controls" @click="openHelp(0)">
                    <span class="help-label">Help</span>
                    <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
                  </div>
                </div>
                <v-progress-linear indeterminate v-if="currentLoading"></v-progress-linear>
                <!-- Grid -->
                <div class="grid-container" style="height:calc(100vh - 210px);">
                  <ag-grid-vue style="height: 100%; width: 100%;"
                               class="ag-theme-material working-draft-grid"
                               :gridOptions="gridOptions"
                               :columnDefs="currentHeaders"
                               :rowData="currentData"
                               :getRowHeight="getRowHeight"
                               :isExternalFilterPresent="shouldRowsBeFiltered"
                               :doesExternalFilterPass="shouldShowRow">
                  </ag-grid-vue>
                </div>
                <!--Dialogs -->
                <comment-history :commentService="commentService"
                                 :showDialog="isCommentsDialogOpen"
                                 @close="closeCommentsDialog"
                                 @commentEntered="refreshCommentIcon">
                </comment-history>
                <assumptions-dialog :showDialog="isAssumptionsDialogOpen"
                                    :calculations="assumptionCalculations"
                                    :assumptionStatement="assumptionStatement"
                                    :maxWidth="1000"
                                    @save="updateAssumptionStatement"
                                    @cancel="closeAssumptionsDialog"></assumptions-dialog>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
    </v-card-actions>
  </v-card>
</template>

<script>
  import BudgetPageHeader from '@/components/budgets/common/BudgetPageHeader';
  import DataFormatter from '@/mixins/DataFormatter';
  import YearGrid from '@/mixins/YearGrid';
  import HelpableMixin from '@/mixins/Helpable.js';

  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';
  import UnresolvedCommentsRowButton from '@/components/base/ag-grid/UnresolvedCommentsRowButton';
  import AssumptionsClickableCell from '@/components/base/ag-grid/AssumptionsClickableCell';

  import WorkingDraftHelp from '@/components/help/WorkingDraftHelp.vue';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import CommentHistory from '@/components/budgets/dialogs/CommentHistory.vue'
  import AssumptionsDialog from '@/components/budgets/general-ledger/dialogs/AssumptionsDialog.vue'
  import ResDialog from '@/components/base/ResDialog.vue';

   import budgetService from '@/services/budget';
  import budgetGLAccountService from '@/services/budget-gl-account';
  import { mapActions, mapGetters } from 'vuex';

  const GROUP_TYPE_HEADER = 'group-type-header';
  const GL_ACCOUNT_GROUP = 'gl-account-group';
  const BUDGET_GL_ACCOUNT = 'budget-gl-account';
  const GROUP_TYPE_TOTAL = 'group-type-total';
  const NET_TOTAL = 'net-total';

  const HAS_UNRESOLVED_COMMENTS = 'hasUnresolvedComments';

  const PER_DOLLAR_HEADER = 'per-dollar-header';
  const TOTAL = 'total';
  const PRIOR_YEAR_TOTAL = 'priorYearTotal';
  const VAR_AMOUNT = 'varianceAmount';
  const VAR_PERCENTAGE = 'variancePercentage';
  const PER_UNIT_HEADER = 'per-unit-header';
  const AMOUNT_PER_UNIT = 'amountPerUnit';
  const PRIOR_AMOUNT_PER_UNIT = 'priorYearAmountPerUnit';
  const VAR_PER_UNIT = 'variancePerUnit';
  const PER_SQFT_HEADER = 'per-sqft-header';
  const AMOUNT_PER_SQFT = 'amountPerSqft';
  const PRIOR_AMOUNT_PER_SQFT = 'priorYearAmountPerSqft';
  const VAR_PER_SQFT = 'variancePerSqft';

  const PRIOR_YEAR_ACTUALS = 'priorYearActuals';

  const PRIOR_YEAR_BUDGET = 'priorYearBudget';


  const CURRENT_YEAR_ACTUALS = 'currentYearActualsTotal';
  
  const REFORECAST_TO_ACTUALS_VAR_AMOUNT = 'reforecastToActualsVarianceDollars';
  const REFORECAST_TO_ACTUALS_VAR_PERCENTAGE = 'reforecastToActualsVariancePercent';
  
  const ACTUALS_AMOUNT_PER_UNIT = 'reforecastToActualsCurrentYearActualsPerUnit';
  
  const REFORECAST_TO_ACTUALS_VAR_PER_UNIT = 'reforecastToActualsVariancePerUnitDollars';
  
  const ACTUALS_AMOUNT_PER_SQFT = 'reforecastToActualsCurrentYearActualsPerSqft';
  const REFORECAST_TO_ACTUALS_VAR_PER_SQFT = 'reforecastToActualsVariancePerSqftDollars';


  const CURRENT_YEAR_BUDGET = 'currentYearBudgetedTotal';

  const REFORECAST_TO_BUDGET_VAR_AMOUNT = 'reforecastToBudgetVarianceDollars';
  const REFORECAST_TO_BUDGET_VAR_PERCENTAGE = 'reforecastToBudgetVariancePercent';

  const REFORECAST_TO_BUDGET_AMOUNT_PER_UNIT = 'reforecastToBudgetCurrentYearBudgetPerUnit';
  const REFORECAST_TO_BUDGET_VAR_PER_UNIT = 'reforecastToBudgetVariancePerUnitDollars';

  const REFORECAST_TO_BUDGET_AMOUNT_PER_SQFT = 'reforecastToBudgetCurrentYearBudgetPerSqft';
  const REFORECAST_TO_BUDGET_VAR_PER_SQFT = 'reforecastToBudgetVariancePerSqftDollars';

  const BUDGET_TO_BUDGET_VAR_AMOUNT = 'budgetToBudgetVarAmount';
  const BUDGET_TO_BUDGET_VAR_PERCENTAGE = 'budgetToBudgetVarPercentage';

  const BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_UNIT = 'budgetToBudgetPriorAmountPerUnit';
  const BUDGET_TO_BUDGET_VAR_PER_UNIT = 'budgetToBudgetVarPerUnit';

  const BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_SQFT = 'budgetToBudgetPriorAmountPerSqft';
  const BUDGET_TO_BUDGET_VAR_PER_SQFT = 'budgetToBudgetVarPerSqft';

  const PRIOR_YEAR_REFORECAST = 'priorYearReforecast';

  const BUDGET_TO_REFORECAST_VAR_AMOUNT = 'budgetToReforecastVarAmount';
  const BUDGET_TO_REFORECAST_VAR_PERCENTAGE = 'budgetToReforecastVarPercentage';

  const BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_UNIT = 'budgetToReforecastPriorAmountPerUnit';
  const BUDGET_TO_REFORECAST_VAR_PER_UNIT = 'budgetToReforecastVarPerUnit';

  const BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_SQFT = 'budgetToReforecastPriorAmountPerSqft';
  const BUDGET_TO_REFORECAST_VAR_PER_SQFT = 'budgetToReforecastVarPerSqft';


  const CALCULATION_SENTENCES = 'calculationSentences';
  const ASSUMPTION_STATEMENT = 'assumptionStatement';

  export default {
    name: 'WorkingDraft',
    mixins: [DataFormatter, YearGrid, HelpableMixin],
    components: {
      BudgetPageHeader,
      AgGridVue,
      ResRadioGroup,
      CommentHistory,
      AssumptionsDialog,
      ResDialog,
      WorkingDraftHelp,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton, // used by AG Grid
      UnresolvedCommentsRowButton, // used by AG Grid
      AssumptionsClickableCell, // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: {
        required: true
      }
    },
    data: function () {
      return {
        GROUP_TYPE_HEADER: GROUP_TYPE_HEADER,
        GL_ACCOUNT_GROUP: GL_ACCOUNT_GROUP,
        BUDGET_GL_ACCOUNT: BUDGET_GL_ACCOUNT,
        GROUP_TYPE_TOTAL: GROUP_TYPE_TOTAL,
        NET_TOTAL: NET_TOTAL,
        PERIOD_INDEX_00_AMOUNT: this.getColId(0),
        PERIOD_INDEX_01_AMOUNT: this.getColId(1),
        PERIOD_INDEX_02_AMOUNT: this.getColId(2),
        PERIOD_INDEX_03_AMOUNT: this.getColId(3),
        PERIOD_INDEX_04_AMOUNT: this.getColId(4),
        PERIOD_INDEX_05_AMOUNT: this.getColId(5),
        PERIOD_INDEX_06_AMOUNT: this.getColId(6),
        PERIOD_INDEX_07_AMOUNT: this.getColId(7),
        PERIOD_INDEX_08_AMOUNT: this.getColId(8),
        PERIOD_INDEX_09_AMOUNT: this.getColId(9),
        PERIOD_INDEX_10_AMOUNT: this.getColId(10),
        PERIOD_INDEX_11_AMOUNT: this.getColId(11),
        HAS_UNRESOLVED_COMMENTS: HAS_UNRESOLVED_COMMENTS,
        PER_DOLLAR_HEADER: PER_DOLLAR_HEADER,
        TOTAL: TOTAL,
        PRIOR_YEAR_TOTAL: PRIOR_YEAR_TOTAL,
        VAR_AMOUNT: VAR_AMOUNT,
        VAR_PERCENTAGE: VAR_PERCENTAGE,
        PER_UNIT_HEADER: PER_UNIT_HEADER,
        AMOUNT_PER_UNIT: AMOUNT_PER_UNIT,
        PRIOR_AMOUNT_PER_UNIT: PRIOR_AMOUNT_PER_UNIT,
        VAR_PER_UNIT: VAR_PER_UNIT,
        PER_SQFT_HEADER: PER_SQFT_HEADER,
        AMOUNT_PER_SQFT: AMOUNT_PER_SQFT,
        PRIOR_AMOUNT_PER_SQFT: PRIOR_AMOUNT_PER_SQFT,
        VAR_PER_SQFT: VAR_PER_SQFT,

        PRIOR_YEAR_ACTUALS: PRIOR_YEAR_ACTUALS,
        PRIOR_YEAR_BUDGET: PRIOR_YEAR_BUDGET,
        BUDGET_TO_BUDGET_VAR_AMOUNT: BUDGET_TO_BUDGET_VAR_AMOUNT,
        BUDGET_TO_BUDGET_VAR_PERCENTAGE: BUDGET_TO_BUDGET_VAR_PERCENTAGE,
        BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_UNIT: BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_UNIT,
        BUDGET_TO_BUDGET_VAR_PER_UNIT: BUDGET_TO_BUDGET_VAR_PER_UNIT,
        BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_SQFT: BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_SQFT,
        BUDGET_TO_BUDGET_VAR_PER_SQFT: BUDGET_TO_BUDGET_VAR_PER_SQFT,
        PRIOR_YEAR_REFORECAST: PRIOR_YEAR_REFORECAST,
        BUDGET_TO_REFORECAST_VAR_AMOUNT: BUDGET_TO_REFORECAST_VAR_AMOUNT,
        BUDGET_TO_REFORECAST_VAR_PERCENTAGE: BUDGET_TO_REFORECAST_VAR_PERCENTAGE,
        BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_UNIT: BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_UNIT,
        BUDGET_TO_REFORECAST_VAR_PER_UNIT: BUDGET_TO_REFORECAST_VAR_PER_UNIT,
        BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_SQFT: BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_SQFT,
        BUDGET_TO_REFORECAST_VAR_PER_SQFT: BUDGET_TO_REFORECAST_VAR_PER_SQFT,

        CURRENT_YEAR_ACTUALS: CURRENT_YEAR_ACTUALS,

        REFORECAST_TO_ACTUALS_VAR_AMOUNT: REFORECAST_TO_ACTUALS_VAR_AMOUNT,
        REFORECAST_TO_ACTUALS_VAR_PERCENTAGE: REFORECAST_TO_ACTUALS_VAR_PERCENTAGE,

        ACTUALS_AMOUNT_PER_UNIT: ACTUALS_AMOUNT_PER_UNIT,

        REFORECAST_TO_ACTUALS_VAR_PER_UNIT: REFORECAST_TO_ACTUALS_VAR_PER_UNIT,

        ACTUALS_AMOUNT_PER_SQFT: ACTUALS_AMOUNT_PER_SQFT,
        REFORECAST_TO_ACTUALS_VAR_PER_SQFT: REFORECAST_TO_ACTUALS_VAR_PER_SQFT,


        CURRENT_YEAR_BUDGET: CURRENT_YEAR_BUDGET,

        REFORECAST_TO_BUDGET_VAR_AMOUNT: REFORECAST_TO_BUDGET_VAR_AMOUNT,
        REFORECAST_TO_BUDGET_VAR_PERCENTAGE: REFORECAST_TO_BUDGET_VAR_PERCENTAGE,

        REFORECAST_TO_BUDGET_AMOUNT_PER_UNIT: REFORECAST_TO_BUDGET_AMOUNT_PER_UNIT,
        REFORECAST_TO_BUDGET_VAR_PER_UNIT: REFORECAST_TO_BUDGET_VAR_PER_UNIT,

        REFORECAST_TO_BUDGET_AMOUNT_PER_SQFT: REFORECAST_TO_BUDGET_AMOUNT_PER_SQFT,
        REFORECAST_TO_BUDGET_VAR_PER_SQFT: REFORECAST_TO_BUDGET_VAR_PER_SQFT,

        CALCULATION_SENTENCES: CALCULATION_SENTENCES,
        ASSUMPTION_STATEMENT: ASSUMPTION_STATEMENT,

        showUnitDetails: true,
        showSqftDetails: false,
        priorBudgetExists: true,
        currentBudgetExists: true,
        priorYearReforecastExists: true,
        showMonths: false,
        showCalculations: false,
        showAssumptions: true,
        fiscalYearStartMonth: 1,
        fiscalYear: null,
        defaultRowHeight: 36,
        defaultColWidth: 116,
        availableColumns: [],
        budgetIterationLastModifiedTimestamp: null,
        zeroRowsDisplayedRadioOptions: [
          { label: 'No', value: 0 },
          { label: 'Yes', value: 1 }
        ],
        zeroRowsDisplayedSelection: 0, // default to do not hide zero rows
        viewOptions: [
          { label: 'Collapsed', value: 0 },
          { label: 'Expanded', value: 1 }
        ],
        selectedViewOption: 0,// default to collapsed view
        currentData: [],
        gridOptions: {
          headerHeight: 36,
          columnDefs: this.availableColumns,
          getRowClass: this.getRowClass,
          getRowNodeId: this.getRowNodeId,
          applyColumnDefOrder: true,
          alignedGrids: [],
          domLayout: 'normal',
          frameworkComponents: {
            'GroupedRowButton': 'GroupedRowButton',
            'UnresolvedCommentsRowButton': 'UnresolvedCommentsRowButton',
            'AssumptionsClickableCell': 'AssumptionsClickableCell'
          },
        },
        columnRefreshIsQueued: false,
        isCommentsDialogOpen: false,
        commentService: null,
        isAssumptionsDialogOpen: false,
        assumptionStatement: '',
        assumptionCalculations: [],
        editingGLAccountId: null,
        isReforecast: false
      }
    },
    methods: {
      ...mapActions(['changeLoading']),
      async loadBudgetSettings() {
        var t = this;
          t.changeLoading(true);

          let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
          t.fiscalYear = settings.year;
          t.fiscalYearStartMonth = settings.startMonth;
          t.isReforecast = Boolean(settings.isReforecast);

         this.refreshDataFilter();

          t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;

          // setup column headers
          let months = t.getFiscalYearMonths(t.fiscalYearStartMonth);

        this.availableColumns = [
          {
            headerName: "", width: 1, maxWidth: 1, pinned: 'left', suppressSizeToFit: true,
            cellStyle: function (params) {
              var style = {};
              switch (params.data.rowType) {
                case t.GROUP_TYPE_HEADER:
                case t.GROUP_TYPE_TOTAL:
                case t.NET_TOTAL:
                  style['margin-left'] = 0;
                  style['margin-top'] = 0;
                  style['padding-left'] = 10;
                  style['font-weight'] = 'bold';
                  style['text-align'] = 'left';
                  if (params.data.subLabel) {
                    style['line-height'] = 'normal';
                  }
                  break;
                default:
                  style['margin'] = 0;
                  style['padding'] = 0;
                  style['width'] = 0;
                  break;
              }
              return style;
            },
            colId: "fakelabel", field: "fakelabel",
            colSpan: function (params) {
              switch (params.data.rowType) {
                case t.GROUP_TYPE_HEADER:
                case t.GROUP_TYPE_TOTAL:
                case t.NET_TOTAL:
                  return 4;
                default:
                  return 1;
              }
            },
            editable: false,
            cellRenderer: function (params) {
              switch (params.data.rowType) {
                case t.GROUP_TYPE_HEADER:
                case t.GROUP_TYPE_TOTAL:
                case t.NET_TOTAL:
                  return params.data.subLabel ? `<span>${params.data.label}</span><br/><span style="font-weight: normal">${params.data.subLabel}</span>` : `<span>${params.data.label}</span>`;
                default:
                  return null;
              }
            }
          },
          {
            headerName: "", width: 16, pinned: 'left', suppressSizeToFit: true,
            cellStyle: function (params) {
              var style = {};
              switch (params.data.rowType) {
                case t.GROUP_TYPE_HEADER:
                case t.GROUP_TYPE_TOTAL:
                case t.NET_TOTAL:
                  style['margin-left'] = 0;
                  style['margin-top'] = 0;
                  style['padding-left'] = 10;
                  style['font-weight'] = 'bold';
                  style['text-align'] = 'left';
                  if (params.data.subLabel) {
                    style['line-height'] = 'normal';
                  }
                  break;
                default:
                  style['margin'] = 0;
                  style['padding'] = 0;
                  break;
              }
              return style;
            },
            colId: "showGlAccountRecords", field: "showGlAccountRecords",
            valueFormatter: function (params) {
              return params.data.hasGlAccountRecords ? params.data.showGlAccountRecords : '';
            },
            cellRendererSelector: t.selectGroupedCellRenderer,
          },
          {
            headerName: "", width: 16, pinned: 'left', suppressSizeToFit: true, cellStyle: { margin: 0, padding: 0 },
            colId: t.HAS_UNRESOLVED_COMMENTS, field: t.HAS_UNRESOLVED_COMMENTS,
            valueFormatter: function (params) {
              return (params.data.rowType == t.GROUP_TYPE_HEADER || params.data.rowType == t.BUDGET_GL_ACCOUNT) && params.data.hasUnresolvedComments ? params.data.hasUnresolvedComments : '';
            },
            cellRendererSelector: t.selectCommentCellRenderer,
          },
          {
            headerName: "", width: 210, pinned: 'left', suppressSizeToFit: true,
            cellStyle: function (params) {
              var style = { 'margin-left': 0, 'margin-top': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'left' };
              if (params.data.subLabel) {
                style['line-height'] = 'normal';
              }
              return style;
            },
            colId: "label", field: "label",
            editable: false,
            cellRenderer: function (params) {
              switch (params.data.rowType) {
                case t.GROUP_TYPE_HEADER:
                case t.GROUP_TYPE_TOTAL:
                case t.NET_TOTAL:
                  return null;
                case t.GL_ACCOUNT_GROUP:
                  return `<span>${params.data.label}</span>`;
                default:
                  return `<span>${params.data.label}</span><br/><span style="font-weight: bold; font-size: 10px;">${params.data.subLabel}</span>`;
              }
            }
          },

          { headerName: months[0], colId: t.PERIOD_INDEX_00_AMOUNT, field: t.PERIOD_INDEX_00_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell left-border-cell', cellClass: 'left-border-cell', hide: !this.showMonths },
          { headerName: months[1], colId: t.PERIOD_INDEX_01_AMOUNT, field: t.PERIOD_INDEX_01_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[2], colId: t.PERIOD_INDEX_02_AMOUNT, field: t.PERIOD_INDEX_02_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[3], colId: t.PERIOD_INDEX_03_AMOUNT, field: t.PERIOD_INDEX_03_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[4], colId: t.PERIOD_INDEX_04_AMOUNT, field: t.PERIOD_INDEX_04_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[5], colId: t.PERIOD_INDEX_05_AMOUNT, field: t.PERIOD_INDEX_05_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[6], colId: t.PERIOD_INDEX_06_AMOUNT, field: t.PERIOD_INDEX_06_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[7], colId: t.PERIOD_INDEX_07_AMOUNT, field: t.PERIOD_INDEX_07_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[8], colId: t.PERIOD_INDEX_08_AMOUNT, field: t.PERIOD_INDEX_08_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[9], colId: t.PERIOD_INDEX_09_AMOUNT, field: t.PERIOD_INDEX_09_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[10], colId: t.PERIOD_INDEX_10_AMOUNT, field: t.PERIOD_INDEX_10_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },
          { headerName: months[11], colId: t.PERIOD_INDEX_11_AMOUNT, field: t.PERIOD_INDEX_11_AMOUNT, width: this.defaultColWidth, minWidth: this.defaultColWidth, editable: false, valueFormatter: t.formatBudgetValueDollar, headerClass: 'summary-header top-lined-cell', hide: !this.showMonths },


          // Budget to Actuals
          {
            headerName: "BUDGET TO ACTUALS: PER DOLLAR",
            colId: t.PER_DOLLAR_HEADER,
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header',
            colSpan: 4, cellStyle: { textAlign: 'center' },
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.TOTAL, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: (this.fiscalYear - 1) + ' ACTUALS', colId: t.PRIOR_YEAR_TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.PRIOR_YEAR_ACTUALS, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR $', colId: t.VAR_AMOUNT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.VAR_AMOUNT, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR %', colId: t.VAR_PERCENTAGE,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.VAR_PERCENTAGE, valueFormatter: t.formatBudgetValueRoundedPercent, editable: false,
              }
            ]
          },
          {
            headerName: "BUDGET TO ACTUALS: PER UNIT",
            colSpan: 3, colId: t.PER_UNIT_HEADER,
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-actuals-header',
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear - 1) + ' ACTUALS', colId: t.PRIOR_AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.PRIOR_AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-actuals-header', cellClass: 'right-border-cell to-actuals-cell',
                field: t.VAR_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "BUDGET TO ACTUALS: PER SQUARE FOOT",
            colSpan: 3, colId: t.PER_SQFT_HEADER,
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-actuals-header',
            children: [
              {
                headerName: t.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: (t.fiscalYear - 1) + ' ACTUALS', colId: t.PRIOR_AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.PRIOR_AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-actuals-header', cellClass: 'right-border-cell to-actuals-cell',
                field: t.VAR_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              }
            ]
          },



          // Reforecast to Actuals
          {
            headerName: "REFORECAST TO CURRENT YEAR ACTUALS: PER DOLLAR",
            colId: t.PER_DOLLAR_HEADER + '-reforecast-to-actuals',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header',
            colSpan: 4, cellStyle: { textAlign: 'center' },
            children: [
              {
                headerName: this.fiscalYear + ' REFORECAST', colId: t.TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.TOTAL, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: this.fiscalYear + ' ACTUALS', colId: t.PRIOR_YEAR_TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.CURRENT_YEAR_ACTUALS, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR $', colId: t.VAR_AMOUNT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.REFORECAST_TO_ACTUALS_VAR_AMOUNT, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR %', colId: t.VAR_PERCENTAGE,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.REFORECAST_TO_ACTUALS_VAR_PERCENTAGE, valueFormatter: t.formatBudgetValueRoundedPercent, editable: false,
              }
            ]
          },
          {
            headerName: "REFORECAST TO CURRENT YEAR ACTUALS: PER UNIT",
            colSpan: 3, colId: t.PER_UNIT_HEADER + '-reforecast-to-actuals',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-actuals-header',
            children: [
              {
                headerName: this.fiscalYear + ' REFORECAST', colId: t.AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: this.fiscalYear + ' ACTUALS', colId: t.PRIOR_AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.ACTUALS_AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-actuals-header', cellClass: 'right-border-cell to-actuals-cell',
                field: t.REFORECAST_TO_ACTUALS_VAR_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "REFORECAST TO CURRENT YEAR ACTUALS: PER SQUARE FOOT",
            colSpan: 3, colId: t.PER_SQFT_HEADER + '-reforecast-to-actuals',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-actuals-header',
            children: [
              {
                headerName: t.fiscalYear + ' REFORECAST', colId: t.AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: t.AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: t.fiscalYear + ' ACTUALS', colId: t.PRIOR_AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-actuals-header', cellClass: 'to-actuals-cell',
                field: t.ACTUALS_AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-actuals-header', cellClass: 'right-border-cell to-actuals-cell',
                field: t.REFORECAST_TO_ACTUALS_VAR_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              }
            ]
          },


          // Budget to Prior Budget
          {
            headerName: "BUDGET TO PRIOR BUDGET: PER DOLLAR",
            colId: t.PER_DOLLAR_HEADER + '-prior-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header',
            colSpan: 4, cellStyle: { textAlign: 'center' },
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.TOTAL, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: (this.fiscalYear - 1) + ' BUDGET', colId: t.PRIOR_YEAR_TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.PRIOR_YEAR_BUDGET, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR $', colId: t.VAR_AMOUNT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.BUDGET_TO_BUDGET_VAR_AMOUNT, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR %', colId: t.VAR_PERCENTAGE,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.BUDGET_TO_BUDGET_VAR_PERCENTAGE, valueFormatter: t.formatBudgetValueRoundedPercent, editable: false,
              }
            ]
          },
          {
            headerName: "BUDGET TO PRIOR BUDGET: PER UNIT",
            colSpan: 3, colId: t.PER_UNIT_HEADER + '-prior-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-prior-header',
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear - 1) + ' BUDGET', colId: t.PRIOR_AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-prior-header', cellClass: 'right-border-cell to-prior-cell',
                field: t.BUDGET_TO_BUDGET_VAR_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "BUDGET TO PRIOR BUDGET: PER SQUARE FOOT",
            colSpan: 3, colId: t.PER_SQFT_HEADER + '-prior-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-prior-header',
            children: [
              {
                headerName: t.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: (t.fiscalYear - 1) + ' BUDGET', colId: t.PRIOR_AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.BUDGET_TO_BUDGET_PRIOR_AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-prior-header', cellClass: 'right-border-cell to-prior-cell',
                field: t.BUDGET_TO_BUDGET_VAR_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              }
            ]
          },


          // Reforecast to Current Year Budget
          {
            headerName: "REFORECAST TO BUDGET: PER DOLLAR",
            colId: t.PER_DOLLAR_HEADER + '-reforecast-to-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header',
            colSpan: 4, cellStyle: { textAlign: 'center' },
            children: [
              {
                headerName: this.fiscalYear + ' REFORECAST', colId: t.TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.TOTAL, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: (this.fiscalYear) + ' BUDGET', colId: t.PRIOR_YEAR_TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.CURRENT_YEAR_BUDGET, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR $', colId: t.VAR_AMOUNT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_VAR_AMOUNT, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR %', colId: t.VAR_PERCENTAGE,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_VAR_PERCENTAGE, valueFormatter: t.formatBudgetValueRoundedPercent, editable: false,
              }
            ]
          },
          {
            headerName: "REFORECAST TO BUDGET: PER UNIT",
            colSpan: 3, colId: t.PER_UNIT_HEADER + '-reforecast-to-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-prior-header',
            children: [
              {
                headerName: this.fiscalYear + ' REFORECAST', colId: t.AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear) + ' BUDGET', colId: t.PRIOR_AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-prior-header', cellClass: 'right-border-cell to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_VAR_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "REFORECAST TO BUDGET: PER SQUARE FOOT",
            colSpan: 3, colId: t.PER_SQFT_HEADER + '-reforecast-to-budget',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-prior-header',
            children: [
              {
                headerName: t.fiscalYear + ' REFORECAST', colId: t.AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: t.AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: (t.fiscalYear) + ' BUDGET', colId: t.PRIOR_AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-prior-header', cellClass: 'to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-prior-header', cellClass: 'right-border-cell to-prior-cell',
                field: t.REFORECAST_TO_BUDGET_VAR_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              }
            ]
          },


          // Budget to Reforecast
          {
            headerName: "BUDGET TO REFORECAST: PER DOLLAR",
            colId: t.PER_DOLLAR_HEADER + '-reforecast',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-reforecast-header',
            colSpan: 4, cellStyle: { textAlign: 'center' },
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: t.TOTAL, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: (this.fiscalYear - 1) + ' REFORECAST', colId: t.PRIOR_YEAR_TOTAL,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: t.PRIOR_YEAR_REFORECAST, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR $', colId: t.VAR_AMOUNT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_VAR_AMOUNT, valueFormatter: t.formatBudgetValueDollar, editable: false,
              },
              {
                headerName: 'VAR %', colId: t.VAR_PERCENTAGE,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_VAR_PERCENTAGE, valueFormatter: t.formatBudgetValueRoundedPercent, editable: false,
              }
            ]
          },
          {
            headerName: "BUDGET TO REFORECAST: PER UNIT",
            colSpan: 3, colId: t.PER_UNIT_HEADER + '-reforecast',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-reforecast-header',
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: t.AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear - 1) + ' REFORECAST', colId: t.PRIOR_AMOUNT_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_UNIT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-reforecast-header', cellClass: 'right-border-cell to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_VAR_PER_UNIT, valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "BUDGET TO REFORECAST: PER SQUARE FOOT",
            colSpan: 3, colId: t.PER_SQFT_HEADER + '-reforecast',
            suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell right-border-cell to-reforecast-header',
            children: [
              {
                headerName: t.fiscalYear + ' BUDGET', colId: t.AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header left-border-cell top-lined-cell to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: t.AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: (t.fiscalYear - 1) + ' REFORECAST', colId: t.PRIOR_AMOUNT_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_PRIOR_AMOUNT_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $', colId: t.VAR_PER_SQFT,
                width: this.defaultColWidth, minWidth: this.defaultColWidth,
                suppressSizeToFit: false, headerClass: 'summary-header top-lined-cell right-border-cell to-reforecast-header', cellClass: 'right-border-cell to-reforecast-cell',
                field: t.BUDGET_TO_REFORECAST_VAR_PER_SQFT, valueFormatter: t.formatBudgetValueDollarWithCents
              }
            ]
          },
          // Calculations/Assumptions
          {
            headerName: 'CALCULATIONS',
            resizable: true,
            wrapText: true,
            width: 20 * 17.5, suppressSizeToFit: true, headerClass: 'summary-header top-lined-cell left-border-cell right-border-cell', cellClass: 'calculation-cell right-border-cell',
            field: t.CALCULATION_SENTENCES,
            colId: t.CALCULATION_SENTENCES,
            hide: !t.showCalculations
          },
          {
            headerName: 'ASSUMPTIONS',
            resizable: true,
            wrapText: true,
            width: 20 * 17.5, suppressSizeToFit: true, headerClass: 'summary-header top-lined-cell left-border-cell right-border-cell', cellClass: 'assumption-cell',
            field: t.ASSUMPTION_STATEMENT,
            colId: t.ASSUMPTION_STATEMENT,
            hide: !t.showAssumptions,
            cellRendererSelector: t.selectAssumptionCellRenderer
          }
        ];

        this.changeLoading(false);
      },
      async loadWorkingDraftData() {
        var t = this;
        t.changeLoading(true);

        let response = await budgetGLAccountService.getWorkingDraft(t.budgetIterationId);
        if (!response || !response.data) {
          console.log(response);
          return;
        }

        t.currentData = [];
        var currentD = [];
        t.priorBudgetExists = response.data.priorBudgetExists;
        t.currentBudgetExists = response.data.currentBudgetExists;
        t.priorYearReforecastExists = response.data.priorYearReforecastExists;


        response.data.types.forEach((groupType) => {
          if (groupType.groupTypeId == 1 || groupType.groupTypeId == 2 || groupType.groups.length > 0) {
            currentD.push(t.initGroupTypeHeaderRow(groupType));

            groupType.groups.forEach((glGroup) => {
              currentD.push(t.initGroupRow(glGroup));

              glGroup.glAccounts.forEach((glAccount) => {
                currentD.push(t.initBudgetGlAccountRow(glAccount));
              });              
            });

            currentD.push(t.initGroupTypeTotalRow(groupType));
            switch (groupType.groupTypeId) {
              case 2:
                currentD.push(t.initNetTotalRow(response.data.netOperatingIncome));
                break;
              case 3:
                if (groupType.groups.length > 0) {
                  currentD.push(t.initNetTotalRow(response.data.netIncome));
                }
                break;
              case 4:
                if (groupType.groups.length > 0) {
                  currentD.push(t.initNetTotalRow(response.data.netIncomeAfterOtherAccounts));
                }
                break;
              default:
                break;
            }
          }
            });

        t.currentData = currentD;

        t.toggleColumnVisibilities();

        t.changeLoading(false);
      },
      initGroupTypeHeaderRow(data) {
        return {
          label: data.label.toUpperCase(),
          rowType: this.GROUP_TYPE_HEADER,
          rowId: this.GROUP_TYPE_HEADER + '-' + data.groupTypeId,
          groupTypeId: data.groupTypeId,
          zeroOnlyRow: data.zeroOnlyRow,
          showGlAccountRecords: false,
        }
      },
      initGroupTypeTotalRow(data) {
        return {
          label: 'TOTAL ' + data.label.toUpperCase() ,
          subLabel: data.subLabel,
          rowType: this.GROUP_TYPE_TOTAL,
          rowId: this.GROUP_TYPE_TOTAL + '-' + data.groupTypeId,
          groupTypeId: data.groupTypeId,
          zeroOnlyRow: data.zeroOnlyRow,
          showGlAccountRecords: false,

          total: data.total,
          priorYearTotal: data.priorYearTotal,
          varianceAmount: data.varianceAmount,
          variancePercentage: data.variancePercentage,

          amountPerUnit: data.amountPerUnit,
          priorYearAmountPerUnit: data.priorYearAmountPerUnit,
          variancePerUnit: data.variancePerUnit,

          amountPerSqft: data.amountPerSqft,
          priorYearAmountPerSqft: data.priorYearAmountPerSqft,
          variancePerSqft: data.variancePerSqft,

          periodIndex00Amount: data.periodIndex00.amount == null ? 0 : data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount == null ? 0 : data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount == null ? 0 : data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount == null ? 0 : data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount == null ? 0 : data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount == null ? 0 : data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount == null ? 0 : data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount == null ? 0 : data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount == null ? 0 : data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount == null ? 0 : data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount == null ? 0 : data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount == null ? 0 : data.periodIndex11.amount,
          
          priorYearActuals: data.priorYearActualsTotal,
          priorYearBudget: data.priorYearBudgetedTotal,

          currentYearActualsTotal: data.currentYearActualsTotal,

          reforecastToActualsVarianceDollars: data.reforecastToActualsVarianceDollars,
          reforecastToActualsVariancePercent: data.reforecastToActualsVariancePercent,
          reforecastToActualsCurrentYearActualsPerUnit: data.reforecastToActualsCurrentYearActualsPerUnit,

          reforecastToActualsVariancePerUnitDollars: data.reforecastToActualsVariancePerUnitDollars,

          reforecastToActualsCurrentYearActualsPerSqft: data.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActualsVariancePerSqftDollars: data.reforecastToActualsVariancePerSqftDollars,

          currentYearBudgetedTotal: data.currentYearBudgetedTotal,

          reforecastToBudgetVarianceDollars: data.reforecastToBudgetVarianceDollars,
          reforecastToBudgetVariancePercent: data.reforecastToBudgetVariancePercent,

          reforecastToBudgetCurrentYearBudgetPerUnit: data.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudgetVariancePerUnitDollars: data.reforecastToBudgetVariancePerUnitDollars,

          reforecastToBudgetCurrentYearBudgetPerSqft: data.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudgetVariancePerSqftDollars: data.reforecastToBudgetVariancePerSqftDollars,


          budgetToBudgetVarAmount: data.budgetToPriorBudgetVarianceDollars,
          budgetToBudgetVarPercentage: data.budgetToPriorBudgetVariancePercent,

          budgetToBudgetPriorAmountPerUnit: data.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToBudgetVarPerUnit: data.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToBudgetPriorAmountPerSqft: data.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToBudgetVarPerSqft: data.budgetToPriorBudgetVariancePerSqftDollars,


          priorYearReforecast: data.priorYearReforecastedTotal,

          budgetToReforecastVarAmount: data.budgetToReforecastVarianceDollars,
          budgetToReforecastVarPercentage: data.budgetToReforecastVariancePercent,

          budgetToReforecastPriorAmountPerUnit: data.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecastVarPerUnit: data.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecastPriorAmountPerSqft: data.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecastVarPerSqft: data.budgetToReforecastVariancePerSqftDollars
        }
      },
      initGroupRow(data) {
        return {
          tag: this.GL_ACCOUNT_GROUP + '-' + data.groupId,
          rowType: this.GL_ACCOUNT_GROUP,
          label: data.label,
          subLabel: data.subLabel,
          rowId: this.GL_ACCOUNT_GROUP + '-' + data.groupId,
          groupId: data.groupId,
          childrenIds: data.glAccounts.map((data) => {         
            return data.glAccountId;
        }),

          hasUnresolvedComments: data.hasUnresolvedComments,
          hasGlAccountRecords: data.hasGlAccountRecords,
          showGlAccountRecords: false,
          zeroOnlyRow: data.zeroOnlyRow,

          total: data.total,
          priorYearTotal: data.priorYearTotal,
          varianceAmount: data.varianceAmount,
          variancePercentage: data.variancePercentage,

          amountPerUnit: data.amountPerUnit,
          priorYearAmountPerUnit: data.priorYearAmountPerUnit,
          variancePerUnit: data.variancePerUnit,

          amountPerSqft: data.amountPerSqft,
          priorYearAmountPerSqft: data.priorYearAmountPerSqft,
          variancePerSqft: data.variancePerSqft,

          periodIndex00Amount: data.periodIndex00.amount == null ? 0 : data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount == null ? 0 : data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount == null ? 0 : data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount == null ? 0 : data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount == null ? 0 : data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount == null ? 0 : data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount == null ? 0 : data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount == null ? 0 : data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount == null ? 0 : data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount == null ? 0 : data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount == null ? 0 : data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount == null ? 0 : data.periodIndex11.amount,

          priorYearActuals: data.priorYearActualsTotal,
          priorYearBudget: data.priorYearBudgetedTotal,

          currentYearActualsTotal: data.currentYearActualsTotal,

          reforecastToActualsVarianceDollars: data.reforecastToActualsVarianceDollars,
          reforecastToActualsVariancePercent: data.reforecastToActualsVariancePercent,
          reforecastToActualsCurrentYearActualsPerUnit: data.reforecastToActualsCurrentYearActualsPerUnit,

          reforecastToActualsVariancePerUnitDollars: data.reforecastToActualsVariancePerUnitDollars,

          reforecastToActualsCurrentYearActualsPerSqft: data.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActualsVariancePerSqftDollars: data.reforecastToActualsVariancePerSqftDollars,

          currentYearBudgetedTotal: data.currentYearBudgetedTotal,

          reforecastToBudgetVarianceDollars: data.reforecastToBudgetVarianceDollars,
          reforecastToBudgetVariancePercent: data.reforecastToBudgetVariancePercent,

          reforecastToBudgetCurrentYearBudgetPerUnit: data.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudgetVariancePerUnitDollars: data.reforecastToBudgetVariancePerUnitDollars,

          reforecastToBudgetCurrentYearBudgetPerSqft: data.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudgetVariancePerSqftDollars: data.reforecastToBudgetVariancePerSqftDollars,

          budgetToBudgetVarAmount: data.budgetToPriorBudgetVarianceDollars,
          budgetToBudgetVarPercentage: data.budgetToPriorBudgetVariancePercent,

          budgetToBudgetPriorAmountPerUnit: data.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToBudgetVarPerUnit: data.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToBudgetPriorAmountPerSqft: data.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToBudgetVarPerSqft: data.budgetToPriorBudgetVariancePerSqftDollars,


          priorYearReforecast: data.priorYearReforecastedTotal,

          budgetToReforecastVarAmount: data.budgetToReforecastVarianceDollars,
          budgetToReforecastVarPercentage: data.budgetToReforecastVariancePercent,

          budgetToReforecastPriorAmountPerUnit: data.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecastVarPerUnit: data.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecastPriorAmountPerSqft: data.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecastVarPerSqft: data.budgetToReforecastVariancePerSqftDollars
        }
      },
      initBudgetGlAccountRow(data) {
        return {
          tag: this.GL_ACCOUNT_GROUP + '-' + data.groupId,
          rowType: this.BUDGET_GL_ACCOUNT,
          label: data.label,
          subLabel: data.subLabel,
          rowId: this.BUDGET_GL_ACCOUNT + '-' + data.glAccountId,
          groupId: data.groupId,
          glAccountId: data.glAccountId,
          hasUnresolvedComments: data.hasUnresolvedComments,
          hasGlAccountRecords: false,
          showGlAccountRecords: this.selectedViewOption == 1,
          calculations: data.calculations,
          calculationSentences: data.calculationSentences,
          calculationHeight: this.calculateRowHeightForStatements(data.calculationSentences),
          assumptionStatement: data.assumptionStatement,
          assumptionHeight: this.calculateRowHeightForStatements(data.assumptionStatement),
          zeroOnlyRow: data.zeroOnlyRow,

          total: data.total,
          priorYearTotal: data.priorYearTotal,
          varianceAmount: data.varianceAmount,
          variancePercentage: data.variancePercentage,

          amountPerUnit: data.amountPerUnit,
          priorYearAmountPerUnit: data.priorYearAmountPerUnit,
          variancePerUnit: data.variancePerUnit,

          amountPerSqft: data.amountPerSqft,
          priorYearAmountPerSqft: data.priorYearAmountPerSqft,
          variancePerSqft: data.variancePerSqft,

          periodIndex00Amount: data.periodIndex00.amount == null ? 0 : data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount == null ? 0 : data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount == null ? 0 : data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount == null ? 0 : data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount == null ? 0 : data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount == null ? 0 : data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount == null ? 0 : data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount == null ? 0 : data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount == null ? 0 : data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount == null ? 0 : data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount == null ? 0 : data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount == null ? 0 : data.periodIndex11.amount,

          priorYearActuals: data.priorYearActualsTotal,
          priorYearBudget: data.priorYearBudgetedTotal,

          currentYearActualsTotal: data.currentYearActualsTotal,

          reforecastToActualsVarianceDollars: data.reforecastToActualsVarianceDollars,
          reforecastToActualsVariancePercent: data.reforecastToActualsVariancePercent,
          reforecastToActualsCurrentYearActualsPerUnit: data.reforecastToActualsCurrentYearActualsPerUnit,

          reforecastToActualsVariancePerUnitDollars: data.reforecastToActualsVariancePerUnitDollars,

          reforecastToActualsCurrentYearActualsPerSqft: data.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActualsVariancePerSqftDollars: data.reforecastToActualsVariancePerSqftDollars,

          currentYearBudgetedTotal: data.currentYearBudgetedTotal,

          reforecastToBudgetVarianceDollars: data.reforecastToBudgetVarianceDollars,
          reforecastToBudgetVariancePercent: data.reforecastToBudgetVariancePercent,

          reforecastToBudgetCurrentYearBudgetPerUnit: data.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudgetVariancePerUnitDollars: data.reforecastToBudgetVariancePerUnitDollars,

          reforecastToBudgetCurrentYearBudgetPerSqft: data.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudgetVariancePerSqftDollars: data.reforecastToBudgetVariancePerSqftDollars,


          budgetToBudgetVarAmount: data.budgetToPriorBudgetVarianceDollars,
          budgetToBudgetVarPercentage: data.budgetToPriorBudgetVariancePercent,

          budgetToBudgetPriorAmountPerUnit: data.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToBudgetVarPerUnit: data.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToBudgetPriorAmountPerSqft: data.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToBudgetVarPerSqft: data.budgetToPriorBudgetVariancePerSqftDollars,


          priorYearReforecast: data.priorYearReforecastedTotal,

          budgetToReforecastVarAmount: data.budgetToReforecastVarianceDollars,
          budgetToReforecastVarPercentage: data.budgetToReforecastVariancePercent,

          budgetToReforecastPriorAmountPerUnit: data.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecastVarPerUnit: data.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecastPriorAmountPerSqft: data.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecastVarPerSqft: data.budgetToReforecastVariancePerSqftDollars
        }
      },
      initNetTotalRow(data) {
        return {
          label: data.label,
          subLabel: data.subLabel,
          rowType: this.NET_TOTAL,
          rowId: this.NET_TOTAL + '-' + data.label + (data.subLabel ? ' ' + data.subLabel : ''),
          zeroOnlyRow: data.zeroOnlyRow,
          showGlAccountRecords: false,

          total: data.total,
          priorYearTotal: data.priorYearTotal,
          varianceAmount: data.varianceAmount,
          variancePercentage: data.variancePercentage,

          amountPerUnit: data.amountPerUnit,
          priorYearAmountPerUnit: data.priorYearAmountPerUnit,
          variancePerUnit: data.variancePerUnit,

          amountPerSqft: data.amountPerSqft,
          priorYearAmountPerSqft: data.priorYearAmountPerSqft,
          variancePerSqft: data.variancePerSqft,

          periodIndex00Amount: data.periodIndex00.amount == null ? 0 : data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount == null ? 0 : data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount == null ? 0 : data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount == null ? 0 : data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount == null ? 0 : data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount == null ? 0 : data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount == null ? 0 : data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount == null ? 0 : data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount == null ? 0 : data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount == null ? 0 : data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount == null ? 0 : data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount == null ? 0 : data.periodIndex11.amount,

          priorYearActuals: data.priorYearActualsTotal,
          priorYearBudget: data.priorYearBudgetedTotal,

          currentYearActualsTotal: data.currentYearActualsTotal,

          reforecastToActualsVarianceDollars: data.reforecastToActualsVarianceDollars,
          reforecastToActualsVariancePercent: data.reforecastToActualsVariancePercent,
          reforecastToActualsCurrentYearActualsPerUnit: data.reforecastToActualsCurrentYearActualsPerUnit,

          reforecastToActualsVariancePerUnitDollars: data.reforecastToActualsVariancePerUnitDollars,

          reforecastToActualsCurrentYearActualsPerSqft: data.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActualsVariancePerSqftDollars: data.reforecastToActualsVariancePerSqftDollars,

          currentYearBudgetedTotal: data.currentYearBudgetedTotal,

          reforecastToBudgetVarianceDollars: data.reforecastToBudgetVarianceDollars,
          reforecastToBudgetVariancePercent: data.reforecastToBudgetVariancePercent,

          reforecastToBudgetCurrentYearBudgetPerUnit: data.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudgetVariancePerUnitDollars: data.reforecastToBudgetVariancePerUnitDollars,

          reforecastToBudgetCurrentYearBudgetPerSqft: data.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudgetVariancePerSqftDollars: data.reforecastToBudgetVariancePerSqftDollars,


          budgetToBudgetVarAmount: data.budgetToPriorBudgetVarianceDollars,
          budgetToBudgetVarPercentage: data.budgetToPriorBudgetVariancePercent,

          budgetToBudgetPriorAmountPerUnit: data.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToBudgetVarPerUnit: data.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToBudgetPriorAmountPerSqft: data.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToBudgetVarPerSqft: data.budgetToPriorBudgetVariancePerSqftDollars,


          priorYearReforecast: data.priorYearReforecastedTotal,

          budgetToReforecastVarAmount: data.budgetToReforecastVarianceDollars,
          budgetToReforecastVarPercentage: data.budgetToReforecastVariancePercent,

          budgetToReforecastPriorAmountPerUnit: data.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecastVarPerUnit: data.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecastPriorAmountPerSqft: data.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecastVarPerSqft: data.budgetToReforecastVariancePerSqftDollars
        }
      },

      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        var showZeroRows = this.zeroRowsDisplayedSelection == 0;
        return ((node.data.rowType == this.GROUP_TYPE_HEADER || node.data.rowType == this.GROUP_TYPE_TOTAL || node.data.rowType == this.NET_TOTAL) && (showZeroRows || !node.data.zeroOnlyRow))
        || ((node.data.rowType == this.GL_ACCOUNT_GROUP && (showZeroRows || !node.data.zeroOnlyRow || node.data.hasUnresolvedComments))
          || (node.data.rowType == this.BUDGET_GL_ACCOUNT && node.data.showGlAccountRecords && (showZeroRows || !node.data.zeroOnlyRow || node.data.hasUnresolvedComments)));
      },
      zeroRowsDisplayedChanged(displayZeroRows) {
        this.zeroRowsDisplayedSelection = displayZeroRows;
        this.toggleColumnVisibilities();
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      selectedViewOptionChanged() {
        var t = this;
        // toggle collapsed/expanded view
        t.selectedViewOption = +(Boolean(!t.selectedViewOption));

        var itemsToUpdate = [];
        var show = t.selectedViewOption == 1;
        t.gridOptions.api.forEachNode(node => {
          if (node.data.rowType == t.BUDGET_GL_ACCOUNT || node.data.rowType == t.GL_ACCOUNT_GROUP) {
            var data = node.data;
            data.showGlAccountRecords = show;
            itemsToUpdate.push(data);
          }                            
        });

        t.gridOptions.api.applyTransaction({ update: itemsToUpdate });

        t.toggleColumnVisibilities();
      },
      initializeCommentService(budgetIterationId, glAccountId) {
        this.commentService = {
          getComments: () => budgetGLAccountService.getComments(budgetIterationId, glAccountId),
          createComment: (note, isFlag, repliesToNoteId) => budgetGLAccountService.createComment(note, isFlag, repliesToNoteId, budgetIterationId, glAccountId),
          resolveComment: (budgetGLAccountNoteId, note) => budgetGLAccountService.resolveComment(budgetGLAccountNoteId, note),
          editComment: (commentId, commentText) => budgetGLAccountService.editComment(commentId, commentText),
          deleteComment: (commentId) => budgetGLAccountService.deleteComment(commentId)
        }
      },
      openCommentsDialog() {
        this.isCommentsDialogOpen = true;
      },
      closeCommentsDialog() {
        this.editingGLAccountId = null;
        this.isCommentsDialogOpen = false;
      },
      async refreshCommentIcon() {
        var t = this;
        var rowHasUnresolvedComments = await budgetGLAccountService.unresolvedFlagExists(t.budgetIterationId, t.editingGLAccountId);

        var rowId = t.BUDGET_GL_ACCOUNT + '-' + t.editingGLAccountId;
        var rowNode = t.gridOptions.api.getRowNode(rowId);
        rowNode.setDataValue(t.HAS_UNRESOLVED_COMMENTS, rowHasUnresolvedComments);

        var groupRowNode = t.gridOptions.api.getRowNode(t.GL_ACCOUNT_GROUP + '-' + rowNode.data.groupId);
        var unresolvedChildren = groupRowNode.data.childrenIds.filter((cid) => {
          var childRowNode = t.gridOptions.api.getRowNode(t.BUDGET_GL_ACCOUNT + '-' + cid);
          var isUnresolved = childRowNode.data.hasUnresolvedComments;
          return isUnresolved;
        });
        groupRowNode.setDataValue(t.HAS_UNRESOLVED_COMMENTS, unresolvedChildren.length > 0);
      },
      openAssumptionsDialog(params) {
        this.isAssumptionsDialogOpen = true;
        this.assumptionStatement = params.assumptionStatement;
        this.assumptionCalculations = (params.calculations || []).slice(0, 2);
      },
      closeAssumptionsDialog() {
        this.editingGLAccountId = null;
        this.isAssumptionsDialogOpen = false;
      },
      async updateAssumptionStatement(newAssumptionStatement) {
        var t = this;
        t.changeLoading(true);
        await budgetGLAccountService.updateAssumptionStatement(t.budgetIterationId, t.editingGLAccountId, newAssumptionStatement);       

        var rowId = t.BUDGET_GL_ACCOUNT + '-' + t.editingGLAccountId;
        var rowNode = t.gridOptions.api.getRowNode(rowId);
        rowNode.setDataValue(t.ASSUMPTION_STATEMENT, newAssumptionStatement);

        t.closeAssumptionsDialog();

        t.toggleColumnVisibilities();
        t.changeLoading(false);
      },
      toggleColumnVisibilities() {
        var t = this;
        var columnDefs = this.currentHeaders;
        columnDefs.forEach(function (colDef) {
          switch (colDef.colId) {
            case t.PERIOD_INDEX_00_AMOUNT:
            case t.PERIOD_INDEX_01_AMOUNT:
            case t.PERIOD_INDEX_02_AMOUNT:
            case t.PERIOD_INDEX_03_AMOUNT:
            case t.PERIOD_INDEX_04_AMOUNT:
            case t.PERIOD_INDEX_05_AMOUNT:
            case t.PERIOD_INDEX_06_AMOUNT:
            case t.PERIOD_INDEX_07_AMOUNT:
            case t.PERIOD_INDEX_08_AMOUNT:
            case t.PERIOD_INDEX_09_AMOUNT:
            case t.PERIOD_INDEX_10_AMOUNT:
            case t.PERIOD_INDEX_11_AMOUNT:
              colDef.hide = !t.showMonths;
              break;
            case t.PER_DOLLAR_HEADER:
              colDef.hide = t.isReforecast;
              colDef.children[0].hide = t.isReforecast;
              colDef.children[1].hide = t.isReforecast;
              colDef.children[2].hide = t.isReforecast;
              colDef.children[3].hide = t.isReforecast;
              break;
            case t.PER_UNIT_HEADER:
            case t.AMOUNT_PER_UNIT:
            case t.PRIOR_AMOUNT_PER_UNIT:
            case t.VAR_PER_UNIT:
              colDef.hide = !t.showUnitDetails || t.isReforecast;
              colDef.children[0].hide = !t.showUnitDetails || t.isReforecast;
              colDef.children[1].hide = !t.showUnitDetails || t.isReforecast;
              colDef.children[2].hide = !t.showUnitDetails || t.isReforecast;
              break;
            case t.PER_SQFT_HEADER:
            case t.AMOUNT_PER_SQFT:
            case t.PRIOR_AMOUNT_PER_SQFT:
            case t.VAR_PER_SQFT:
              colDef.hide = !t.showSqftDetails || t.isReforecast;
              colDef.children[0].hide = !t.showSqftDetails || t.isReforecast;
              colDef.children[1].hide = !t.showSqftDetails || t.isReforecast;
              colDef.children[2].hide = !t.showSqftDetails || t.isReforecast;
              break;
            case t.PER_DOLLAR_HEADER + '-prior-budget':
              colDef.hide = !t.priorBudgetExists || t.isReforecast;
              colDef.children[0].hide = !t.priorBudgetExists || t.isReforecast;
              colDef.children[1].hide = !t.priorBudgetExists || t.isReforecast;
              colDef.children[2].hide = !t.priorBudgetExists || t.isReforecast;
              colDef.children[3].hide = !t.priorBudgetExists || t.isReforecast;
              break;
            case t.PER_UNIT_HEADER + '-prior-budget':
              colDef.hide = !t.priorBudgetExists || !t.showUnitDetails || t.isReforecast;
              colDef.children[0].hide = !t.priorBudgetExists || !t.showUnitDetails || t.isReforecast;
              colDef.children[1].hide = !t.priorBudgetExists || !t.showUnitDetails || t.isReforecast;
              colDef.children[2].hide = !t.priorBudgetExists || !t.showUnitDetails || t.isReforecast;
              break;
            case t.PER_SQFT_HEADER + '-prior-budget':
              colDef.hide = !t.priorBudgetExists || !t.showSqftDetails || t.isReforecast;
              colDef.children[0].hide = !t.priorBudgetExists || !t.showSqftDetails || t.isReforecast;
              colDef.children[1].hide = !t.priorBudgetExists || !t.showSqftDetails || t.isReforecast;
              colDef.children[2].hide = !t.priorBudgetExists || !t.showSqftDetails || t.isReforecast;
              break;

            case t.PER_DOLLAR_HEADER + '-reforecast-to-actuals':
              colDef.hide = !t.isReforecast;
              colDef.children[0].hide = !t.isReforecast;
              colDef.children[1].hide = !t.isReforecast;
              colDef.children[2].hide = !t.isReforecast;
              colDef.children[3].hide = !t.isReforecast;
              break;
            case t.PER_UNIT_HEADER + '-reforecast-to-actuals':
              colDef.hide = !t.isReforecast || !t.showUnitDetails;
              colDef.children[0].hide = !t.isReforecast || !t.showUnitDetails;
              colDef.children[1].hide = !t.isReforecast || !t.showUnitDetails;
              colDef.children[2].hide = !t.isReforecast || !t.showUnitDetails;
              break;
            case t.PER_SQFT_HEADER + '-reforecast-to-actuals':
              colDef.hide = !t.isReforecast || !t.showSqftDetails;
              colDef.children[0].hide = !t.isReforecast || !t.showSqftDetails;
              colDef.children[1].hide = !t.isReforecast || !t.showSqftDetails;
              colDef.children[2].hide = !t.isReforecast || !t.showSqftDetails;
              break;

            case t.PER_DOLLAR_HEADER + '-reforecast-to-budget':
              colDef.hide = !t.isReforecast || !t.currentBudgetExists;
              colDef.children[0].hide = !t.isReforecast || !t.currentBudgetExists;
              colDef.children[1].hide = !t.isReforecast || !t.currentBudgetExists;
              colDef.children[2].hide = !t.isReforecast || !t.currentBudgetExists;
              colDef.children[3].hide = !t.isReforecast || !t.currentBudgetExists;
              break;
            case t.PER_UNIT_HEADER + '-reforecast-to-budget':
              colDef.hide = !t.isReforecast || !t.showUnitDetails || !t.currentBudgetExists;
              colDef.children[0].hide = !t.isReforecast || !t.showUnitDetails || !t.currentBudgetExists;
              colDef.children[1].hide = !t.isReforecast || !t.showUnitDetails || !t.currentBudgetExists;
              colDef.children[2].hide = !t.isReforecast || !t.showUnitDetails || !t.currentBudgetExists;
              break;
            case t.PER_SQFT_HEADER + '-reforecast-to-budget':
              colDef.hide = !t.isReforecast || !t.showSqftDetails || !t.currentBudgetExists;
              colDef.children[0].hide = !t.isReforecast || !t.showSqftDetails || !t.currentBudgetExists;
              colDef.children[1].hide = !t.isReforecast || !t.showSqftDetails || !t.currentBudgetExists;
              colDef.children[2].hide = !t.isReforecast || !t.showSqftDetails || !t.currentBudgetExists;
              break;

            case t.PER_DOLLAR_HEADER + '-reforecast':
              colDef.hide = !t.priorBudgetExists || t.isReforecast;
              colDef.children[0].hide = !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[1].hide = !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[2].hide = !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[3].hide = !t.priorYearReforecastExists || t.isReforecast;
              break;
            case t.PER_UNIT_HEADER + '-reforecast':
              colDef.hide = !t.showUnitDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[0].hide = !t.showUnitDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[1].hide = !t.showUnitDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[2].hide = !t.showUnitDetails || !t.priorYearReforecastExists || t.isReforecast;
              break;
            case t.PER_SQFT_HEADER + '-reforecast':
              colDef.hide = !t.showSqftDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[0].hide = !t.showSqftDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[1].hide = !t.showSqftDetails || !t.priorYearReforecastExists || t.isReforecast;
              colDef.children[2].hide = !t.showSqftDetails || !t.priorYearReforecastExists || t.isReforecast;
              break;
            case t.CALCULATION_SENTENCES:
              colDef.hide = !t.showCalculations;
              break;
            case t.ASSUMPTION_STATEMENT:
              colDef.hide = !t.showAssumptions;
              break;
            default:
              break;
          }

          if (t.anyOptionalColumnVisible && (colDef.colId == t.PER_DOLLAR_HEADER || colDef.colId.startsWith(t.PER_DOLLAR_HEADER) || colDef.colId == t.PER_UNIT_HEADER || colDef.colId == t.PER_SQFT_HEADER)) {
            for (var i = 0; i < colDef.children.length; i++) {
              colDef.children[i].width = t.defaultColWidth;
            }
          }

          if ((t.showMonths) && (colDef.colId == t.PERIOD_INDEX_00_AMOUNT || colDef.colId == t.PERIOD_INDEX_01_AMOUNT || colDef.colId == t.PERIOD_INDEX_02_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_03_AMOUNT || colDef.colId == t.PERIOD_INDEX_04_AMOUNT || colDef.colId == t.PERIOD_INDEX_05_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_06_AMOUNT || colDef.colId == t.PERIOD_INDEX_07_AMOUNT || colDef.colId == t.PERIOD_INDEX_08_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_09_AMOUNT || colDef.colId == t.PERIOD_INDEX_10_AMOUNT || colDef.colId == t.PERIOD_INDEX_11_AMOUNT)) {
            colDef.width = t.defaultColWidth;
          }

          if (t.showCalculations || t.showAssumptions) {
            t.gridOptions.columnApi.autoSizeColumns([t.CALCULATION_SENTENCES, t.ASSUMPTION_STATEMENT]);
          }
        });

        t.gridOptions.api.setColumnDefs(columnDefs);

        t.gridOptions.api.resetRowHeights();

        if (!t.showMonths) {
          t.gridOptions.api.sizeColumnsToFit();
        }

        t.refreshDataFilter();
      },
      calculateRowHeightForStatements(sentence) {
        if (sentence == null) { return this.defaultRowHeight; }
        var height = this.defaultRowHeight;
        if (sentence.length > 0) {
          var sentencearray = sentence.split(/\n/g);
          var newHeight = 0;
          for (var i = 0; i < sentencearray.length; i++) {
            newHeight = newHeight + ((sentencearray[i].length * 7) / 326 * 15);            
          }
          if (sentencearray.length > 1 && (sentencearray[0].length < 93 && sentencearray[1].length < 93)) {
            newHeight = newHeight + 15; // add extra space for line break
          }
          height = this.defaultRowHeight > newHeight ? this.defaultRowHeight : newHeight;
        }
        return height;
      },
      getRowHeight(params) {
        switch (params.data.rowType) {
          case this.GROUP_TYPE_HEADER:
            if (params.data.groupTypeId == 2) {
              return this.defaultRowHeight;
            } else {
              return this.defaultRowHeight + 20;
            }
          case this.GROUP_TYPE_TOTAL:
            return this.defaultRowHeight + 20;
          case this.BUDGET_GL_ACCOUNT:
            if (this.showCalculations && this.showAssumptions) {
              return params.data.calculationHeight > params.data.assumptionHeight ? params.data.calculationHeight : params.data.assumptionHeight;
            } else if (this.showCalculations) {
              return params.data.calculationHeight;
            } else if (this.showAssumptions) {
              return params.data.assumptionHeight;
            } else {
              return this.defaultRowHeight;
            }
          default:
            return this.defaultRowHeight;
        }
      },

      getRowClass(params) {
        switch (params.data.rowType) {
          // readonly top line
          case this.NET_TOTAL:
            return 'sub-header-row top-lined-row bottom-lined-row';
          case this.GROUP_TYPE_TOTAL:
            return 'readonly-row top-lined-row bottom-spaced-row';
          case this.GROUP_TYPE_HEADER:
            if (params.data.groupTypeId == 2) {
              return 'readonly-row';
            } else {
              return 'readonly-row  top-spaced-row';
            }            
          case this.GL_ACCOUNT_GROUP:
          case this.BUDGET_GL_ACCOUNT: 
          default:
            return 'readonly-row';
        }
      },

      formatBudgetValueDollar(params) {
        return params.data.rowType == this.GROUP_TYPE_HEADER ? '' : this.formatCurrency(params.value, true, true);
      },
      formatBudgetValueDollarWithCents(params) {
        return params.data.rowType == this.GROUP_TYPE_HEADER ? '' : this.formatCurrency(params.value, false, true);
      },
      formatBudgetValueRoundedPercent(params) {
        return params.data.rowType == this.GROUP_TYPE_HEADER ? '' : this.formatPercentage(params.value, true, true);
      },

      getColId(periodIndex) {
        return periodIndex < 10 ? "periodIndex0" + periodIndex + "Amount" : "periodIndex" + periodIndex + "Amount";
      },
      selectAssumptionCellRenderer(params) {
        let t = this;
        if (params.data.rowType == t.BUDGET_GL_ACCOUNT) {
          return {
            component: 'AssumptionsClickableCell',
            params: {
              contents: params.value == null ? '' : params.value,
              onClick: function (params) {               
                if (params.data.rowType == t.BUDGET_GL_ACCOUNT) {
                  t.editingGLAccountId = params.data.glAccountId;
                  t.openAssumptionsDialog(params.data);
                }
              }
            }
          }
        } else {
          return null;
        }
      },
      selectCommentCellRenderer(params) {
        let t = this;
        if ((params.data.rowType == t.BUDGET_GL_ACCOUNT || params.data.rowType == t.GL_ACCOUNT_GROUP) && params.data.hasUnresolvedComments) {
          return {
            component: 'UnresolvedCommentsRowButton',
            params: {
              clickable: function () { return params.data.rowType == t.BUDGET_GL_ACCOUNT; },
              onClick: function (params) {
                if (params.data.rowType == t.BUDGET_GL_ACCOUNT) {
                  t.editingGLAccountId = params.data.glAccountId;
                  t.initializeCommentService(t.budgetIterationId, params.data.glAccountId);
                  t.openCommentsDialog();
                }
              },
            }
          }
        } else {
          return null;
        }
      },
      selectGroupedCellRenderer(params) {
        let t = this;
        if (params.data.rowType == t.GL_ACCOUNT_GROUP && params.data.hasGlAccountRecords) {
          return {
            component: 'GroupedRowButton',
            params: {
              show: function () { return true; },
              allCollapsed: function (params) {
                return !params.data.showGlAccountRecords;
              },
              onClick: function (params) {
                let show = !params.data.showGlAccountRecords;

                params.api.forEachNode(node => {
                  if (node.data.tag == params.data.tag) {
                    node.setDataValue('showGlAccountRecords', show);
                  }
                });

                t.refreshDataFilter();
              },
            }
          }
        } 
      else {
          return null;
        }
      },
      getRowNodeId(data) {
        return data.rowId;
      },
      adjustGridVerticalPosition() {
        var addColumnsControlContainer = this.$el.querySelector('.help-controls');
        var gridContainer = this.$el.querySelector('.grid-container');
        // detect element wrapping
        if (addColumnsControlContainer && addColumnsControlContainer.offsetTop > addColumnsControlContainer.parentElement.offsetTop) {
          // give the 2nd row some space
          if (gridContainer) {
            gridContainer.style.marginTop = '50px';
          }
        }
        else {
          if (gridContainer) {
            gridContainer.style.marginTop = '0px';
          }
        }
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      currentHeaders() {
        return this.availableColumns;
      },
      anyOptionalColumnVisible() {
        return this.showMonths || this.showCalculations || this.showAssumptions || this.showUnitDetails || this.showSqftDetails || this.currentBudgetExists || this.priorBudgetExists || this.priorYearReforecastExists;
      },
    },
    async mounted() {
      // configure settings
      await this.loadBudgetSettings();

      // load budgets
      await this.loadWorkingDraftData();

      window.addEventListener('resize', this.adjustGridVerticalPosition);
      setTimeout(this.adjustGridVerticalPosition);
    },
    unmounted() {
      window.removeEventListener('resize', this.adjustGridVerticalPosition);
    },
    created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.loadBudgetSettings);
    },
    watch: {
      async $route(to, from) {
        let budgetChanged = to.params.budgetIterationId != from.params.budgetIterationId;

        if (budgetChanged) {       
          this.showMonths = false;
          this.showCalculations = false;
          this.showAssumptions = true;
          this.showUnitDetails = true;
          this.showSqftDetails = false;

          this.loadBudgetSettings();

          await this.loadWorkingDraftData();
        }
      },
      anyOptionalColumnVisible: function () {
        this.toggleColumnVisibilities();
      },
    }
  };
</script>

<style lang="scss" scoped>
  .center {
    height: 30em;
    top: 50%;
    text-align: center;
  }
    .center div[role="progressbar"]{
      top: 50%;
    }
  div[role="progressbar"] {
     margin-top: -4px;
  }
  .v-input--selection-controls::v-deep { margin: 0 !important; padding: 0 !important; }

  .grid-controls {
    width: 100%;
    height: 40px;
    margin-bottom: 14px;
    margin-top: -8px;
  }

  .grid-radio-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 6px 0 0 10px;
    height: 100%;
  }

  .add-columns-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    vertical-align: top;
    height: 100%;
  }

  .add-columns-label {
    vertical-align: top;
    display: inline-block;
    padding: 7px 8px 0 10px;
    height: 100%;
  }

  .add-columns-option::v-deep {
    display: inline-block;
    .v-label

  {
    font-size: 13px !important;
    color: var(--v-resGrey-darken2);
    font-weight: bold;
    padding-right: 14px;
    padding-top: 8px;
  }

  .v-input--selection-controls__input {
    margin-top: 0;
    padding-top: 8px;
  }

  .v-messages {
    display: none;
  }

  }

  .help-controls {
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }

  .help-label {
    padding-right: 6px;
  }

  @media (min-width: 1264px) {
    .container {
      max-width: 1285px;
    }
  }

  .calculation-cell,
  .assumption-cell {
    word-break: keep-all !important;
  }

  .assumption-cell,
  .calculation-cell {
    white-space: break-spaces !important;
    font-size: 12px;
    line-height: 14px;
    font-weight: 450;
  }

  div[col-id="assumptionStatement"] {
    line-height: 14px !important;
  }

  div[col-id="calculationSentences"] {
    line-height: 14px !important;
  }
  .gl-data-cell {
      font-weight:450;
  }
</style>

<style>
  .to-actuals-header,
  .to-reforecast-header {
    background-color: #d9eaf0;
  }

  .to-actuals-cell,
  .to-reforecast-cell {
    background-color: #DFEAF0;
  }

  .to-prior-header {
    background-color: #e8e8e8;
  }

  .to-prior-cell {
    background-color: #f2f2f2;
  }
</style>
